import React, { useState, useEffect, useRef } from "react";
import { MessageSquare, Briefcase, Mail, MapPin, Bug } from "lucide-react";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  limit,
  serverTimestamp,
  doc,
  getDocs,
  updateDoc,
  where,
  addDoc,
  arrayUnion,
} from "firebase/firestore";
import { db } from "../firebase";
import { ChatMessage, ServiceOffer, getDistance } from "../utils/helpers";
import { useAppContext } from "../context/AppContext";

// 🚀 ІМПОРТ ВСІХ НАШИХ РОЗДІЛЕНИХ КОМПОНЕНТІВ
import Admin from "./Admin";
import FeedTab from "../components/FeedTab";
import LocalChatTab from "../components/LocalChatTab";
import ServicesTab from "../components/ServicesTab";
import DmInbox from "../components/dm/DmInbox";
import DmRoom from "../components/dm/DmRoom";
import EulaModal from "../components/modals/EulaModal";
import BugReportModal from "../components/modals/BugReportModal";
import AddServiceModal from "../components/modals/AddServiceModal";
import CommentsModal from "../components/modals/CommentsModal";
import PetProfileModal from "../components/modals/PetProfileModal";
import CommunityModal from "../components/modals/CommunityModal";

export default function Chat() {
  const { lang, t, user } = useAppContext();
  const isAdmin =
    user?.uid === "uhTrLUhzCjallqNDIof3JudJr1p2" ||
    user?.uid === "vl1DV8Azfmhz0xWznwnYJC37LBi2";

  // ================= СТЕЙТИ БАЗИ ДАНИХ =================
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [inboxMessages, setInboxMessages] = useState<any[]>([]);
  const [servicesList, setServicesList] = useState<ServiceOffer[]>([]);
  const [posts, setPosts] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [followingList, setFollowingList] = useState<string[]>([]);
  const [blockedUsers, setBlockedUsers] = useState<string[]>([]);
  const [reportedContent, setReportedContent] = useState<string[]>([]);

  // ================= СТЕЙТИ НАВІГАЦІЇ =================
  const [chatTab, setChatTab] = useState<"global" | "local">("global");
  const [serviceTab, setServiceTab] = useState<"global" | "local">("local");
  const [showServices, setShowServices] = useState(false);
  const [chatRadius, setChatRadius] = useState(25);

  // ================= СТЕЙТИ МОДАЛОК =================
  const [showEulaModal, setShowEulaModal] = useState(
    !localStorage.getItem("mypaws_eula_accepted")
  );
  const [showBugModal, setShowBugModal] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [showDmInbox, setShowDmInbox] = useState(false);
  const [showAddServiceForm, setShowAddServiceForm] = useState(false);
  const [commentPostId, setCommentPostId] = useState<string | null>(null);

  // ================= СТЕЙТИ ПРОФІЛІВ ТА DM =================
  const [activeDmRoom, setActiveDmRoom] = useState<any>(null);
  const [selectedChatUser, setSelectedChatUser] = useState<any | null>(null);
  const [selectedChatPetDetails, setSelectedChatPetDetails] =
    useState<any>(null);

  // ================= ГЕОЛОКАЦІЯ =================
  const [isLocating, setIsLocating] = useState(true);
  const [userCoords, setUserCoords] = useState<[number, number] | null>(null);
  const mapCenterObj = { coords: [50.4501, 30.5234] as [number, number] };
  const chatEndRef = useRef<HTMLDivElement>(null);

  // ================= ЕФЕКТИ (ПІДПИСКИ FIREBASE) =================
  useEffect(() => {
    if (!user) return;
    setIsLocating(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserCoords([pos.coords.latitude, pos.coords.longitude]);
          setIsLocating(false);
        },
        () => setIsLocating(false)
      );
    } else setIsLocating(false);
    const timer = setTimeout(() => setIsLocating(false), 2000);

    const unsubChat = onSnapshot(
      query(
        collection(db, "local_chat"),
        orderBy("createdAt", "asc"),
        limit(50)
      ),
      (snap) =>
        setChatMessages(
          snap.docs.map(
            (d) =>
              ({
                id: d.id,
                ...d.data(),
                isMe: d.data().userId === user.uid,
              } as any)
          )
        )
    );
    const unsubServices = onSnapshot(
      query(collection(db, "services"), orderBy("createdAt", "desc")),
      (snap) =>
        setServicesList(
          snap.docs.map((d) => ({ id: d.id, ...d.data() } as any))
        )
    );
    const unsubDmSent = onSnapshot(
      query(
        collection(db, "direct_messages"),
        where("senderId", "==", user.uid)
      ),
      (snap) =>
        setInboxMessages((prev) =>
          [
            ...snap.docs.map((d) => ({ id: d.id, ...d.data() } as any)),
            ...prev.filter((m) => m.recipientId === user.uid),
          ].sort(
            (a, b) =>
              (a.createdAt?.toMillis() || 0) - (b.createdAt?.toMillis() || 0)
          )
        )
    );
    const unsubDmRecv = onSnapshot(
      query(
        collection(db, "direct_messages"),
        where("recipientId", "==", user.uid)
      ),
      (snap) =>
        setInboxMessages((prev) =>
          [
            ...prev.filter((m) => m.senderId === user.uid),
            ...snap.docs.map((d) => ({ id: d.id, ...d.data() } as any)),
          ].sort(
            (a, b) =>
              (a.createdAt?.toMillis() || 0) - (b.createdAt?.toMillis() || 0)
          )
        )
    );
    const unsubPosts = onSnapshot(
      query(collection(db, "posts"), orderBy("createdAt", "desc"), limit(50)),
      (snap) => setPosts(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    const unsubAllUsers = onSnapshot(collection(db, "users"), (snap) =>
      setAllUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    const unsubUser = onSnapshot(doc(db, "users", user.uid), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setFollowingList(data.following || []);
        setBlockedUsers(data.blockedUsers || []);
      }
    });

    return () => {
      clearTimeout(timer);
      unsubChat();
      unsubServices();
      unsubDmSent();
      unsubDmRecv();
      unsubPosts();
      unsubUser();
      unsubAllUsers();
    };
  }, [user]);

  // СВІЖІ ДАНІ ПЕСИКА
  useEffect(() => {
    if (selectedChatUser?.id) {
      getDocs(collection(db, "users", selectedChatUser.id, "pets"))
        .then((snap) => {
          if (!snap.empty) setSelectedChatPetDetails(snap.docs[0].data());
          else setSelectedChatPetDetails(null);
        })
        .catch((err) => console.log(err));
    } else setSelectedChatPetDetails(null);
  }, [selectedChatUser]);

  // ПРОЧИТАННЯ ПОВІДОМЛЕНЬ
  useEffect(() => {
    if (activeDmRoom && user)
      inboxMessages.forEach(async (m) => {
        if (
          m.roomId === activeDmRoom.id &&
          m.recipientId === user.uid &&
          !m.read
        )
          await updateDoc(doc(db, "direct_messages", m.id), { read: true });
      });
  }, [activeDmRoom, inboxMessages, user]);

  // ================= ГЛОБАЛЬНІ ОБРОБНИКИ =================
  const handleToggleFollow = async (targetUserId: string) => {
    if (!user) return;
    const userRef = doc(db, "users", user.uid);
    if (followingList.includes(targetUserId))
      await updateDoc(userRef, {
        following: followingList.filter((id) => id !== targetUserId),
      });
    else
      await updateDoc(userRef, { following: [...followingList, targetUserId] });
  };

  const handleBlockUser = async (targetId: string) => {
    if (!user) return;
    if (
      window.confirm(
        lang === "uk" ? "Заблокувати цього користувача?" : "Block this user?"
      )
    ) {
      await updateDoc(doc(db, "users", user.uid), {
        blockedUsers: arrayUnion(targetId),
      });
      setSelectedChatUser(null);
      alert(lang === "uk" ? "Користувача заблоковано." : "User blocked.");
    }
  };

  const handleReport = async (
    contentType: string,
    contentId: string,
    reportedUserId: string
  ) => {
    if (!user) return;
    if (
      window.confirm(
        lang === "uk" ? "Поскаржитися на цей контент?" : "Report this content?"
      )
    ) {
      await addDoc(collection(db, "reports"), {
        reporterId: user.uid,
        reportedUserId,
        contentId,
        contentType,
        createdAt: serverTimestamp(),
      });
      setReportedContent((prev) => [...prev, contentId]);
      alert(lang === "uk" ? "Відправлено на модерацію." : "Reported.");
    }
  };

  const handleStartDM = (targetUser: any) => {
    if (!user || targetUser.id === user.uid) return;
    const roomId = [user.uid, targetUser.id].sort().join("_");
    setActiveDmRoom({
      id: roomId,
      otherUserId: targetUser.id,
      otherUserName: targetUser.name,
      otherUserPhoto: targetUser.photo,
    });
    setSelectedChatUser(null);
  };

  // ================= ФІЛЬТРАЦІЯ =================
  const safeChatMessages = chatMessages.filter(
    (m) =>
      !blockedUsers.includes(m.userId || "") &&
      !reportedContent.includes(String(m.id))
  );
  const safePosts = posts.filter(
    (p) =>
      !blockedUsers.includes(p.authorId || "") &&
      !reportedContent.includes(String(p.id))
  );
  const safeServices = servicesList.filter(
    (s) =>
      !blockedUsers.includes(s.userId || "") &&
      !reportedContent.includes(String(s.id))
  );

  const unreadDmCount = inboxMessages.filter(
    (m) =>
      m.recipientId === user?.uid &&
      !m.read &&
      !blockedUsers.includes(m.senderId)
  ).length;
  const inboxRooms = Array.from(
    inboxMessages
      .reduce((map, msg) => {
        if (
          !map.has(msg.roomId) ||
          (map.get(msg.roomId).createdAt?.toMillis() || 0) <
            (msg.createdAt?.toMillis() || 0)
        )
          map.set(msg.roomId, msg);
        return map;
      }, new Map())
      .values()
  )
    .sort(
      (a: any, b: any) =>
        (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0)
    )
    .filter(
      (r: any) =>
        !blockedUsers.includes(r.senderId) &&
        !blockedUsers.includes(r.recipientId)
    );

  const knownUsersMap = new Map();
  posts.forEach((p) => {
    if (p.authorId)
      knownUsersMap.set(p.authorId, {
        id: p.authorId,
        name: p.authorName,
        photo: p.authorPhoto,
        breed: p.authorBreed,
      });
  });
  chatMessages.forEach((c) => {
    if (c.userId)
      knownUsersMap.set(c.userId, {
        id: c.userId,
        name: c.author,
        photo: c.petPhoto,
        breed: c.breed,
      });
  });
  inboxRooms.forEach((r: any) => {
    const isMe = r.senderId === user?.uid;
    const otherId = isMe ? r.recipientId : r.senderId;
    if (otherId)
      knownUsersMap.set(otherId, {
        id: otherId,
        name: isMe ? r.recipientName : r.senderName,
        photo: isMe ? r.recipientPhoto : r.senderPhoto,
      });
  });
  allUsers.forEach((u) => {
    if (u.id && !knownUsersMap.has(u.id))
      knownUsersMap.set(u.id, {
        id: u.id,
        name: u.ownerName || "Користувач",
        photo: null,
        breed: "",
      });
  });
  const knownUsers = Array.from(knownUsersMap.values()).filter(
    (u: any) => u.id !== user?.uid && !blockedUsers.includes(u.id)
  );

  const displayedChatMessages = safeChatMessages.filter(
    (msg) =>
      msg.isMe ||
      (msg.coords &&
        (userCoords || mapCenterObj.coords) &&
        getDistance(
          (userCoords || mapCenterObj.coords)[0],
          (userCoords || mapCenterObj.coords)[1],
          msg.coords[0],
          msg.coords[1]
        ) <= chatRadius)
  );
  const displayedServices = safeServices.filter(
    (svc) =>
      serviceTab === "global" ||
      (svc.coords &&
        (userCoords || mapCenterObj.coords) &&
        getDistance(
          (userCoords || mapCenterObj.coords)[0],
          (userCoords || mapCenterObj.coords)[1],
          svc.coords[0],
          svc.coords[1]
        ) <= chatRadius)
  );

  return (
    <div className="flex flex-col h-[100dvh] absolute inset-0 z-20 bg-[var(--bg-soft)] animate-fade-in">
      {/* HEADER НАВІГАЦІЇ */}
      <div className="bg-[var(--bg-card)] border-b border-[var(--border-color)] px-5 py-4 shrink-0 pt-12 shadow-sm relative z-40">
        <div className="flex justify-between items-center">
          <h2 className="font-extrabold text-xl flex items-center gap-2 text-[var(--text-main)]">
            {showServices ? (
              <Briefcase className="text-[#a073e7]" />
            ) : chatTab === "global" ? (
              <MessageSquare className="text-[#a073e7]" />
            ) : (
              <MapPin className="text-[#a073e7]" />
            )}
            {showServices
              ? t.services
              : chatTab === "global"
              ? lang === "uk"
                ? "Стрічка"
                : "Feed"
              : t.radar}
          </h2>
          <div className="flex items-center gap-2">
            {!isAdmin && (
              <button
                onClick={() => setShowBugModal(true)}
                className="relative w-[38px] h-[38px] flex items-center justify-center bg-[var(--bg-soft)] text-[#a073e7] border border-[var(--border-color)] rounded-xl shadow-sm"
              >
                <Bug size={18} />
              </button>
            )}
            {isAdmin && (
              <button
                onClick={() => setShowAdminPanel(true)}
                className="relative w-[38px] h-[38px] flex items-center justify-center bg-[var(--bg-soft)] border border-[var(--border-color)] rounded-xl shadow-sm overflow-hidden"
              >
                <img
                  src="/admin-icon.png"
                  alt="Admin"
                  className="w-5 h-5 object-contain"
                />
              </button>
            )}
            <button
              onClick={() => setShowDmInbox(true)}
              className="relative w-[38px] h-[38px] flex items-center justify-center bg-[var(--bg-soft)] text-[#a073e7] border border-[var(--border-color)] rounded-xl shadow-sm"
            >
              <Mail size={18} />
              {unreadDmCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--red-text)] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-[var(--red-text)] border-2 border-[var(--bg-card)]"></span>
                </span>
              )}
            </button>
            <button
              onClick={() => setShowServices(!showServices)}
              className={`h-[38px] px-4 border rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 shadow-sm ${
                showServices
                  ? "bg-[var(--inverted-btn)] text-[var(--inverted-text)] border-[var(--inverted-btn)]"
                  : "bg-[var(--bg-soft)] text-[#a073e7] border-[var(--border-color)]"
              }`}
            >
              <Briefcase size={16} /> {showServices ? t.chat : t.services}
            </button>
          </div>
        </div>
        {((!showServices && chatTab === "local") || showServices) && (
          <div className="mt-4 flex flex-col gap-2">
            <div className="flex justify-between items-center text-[var(--text-muted)] text-xs font-bold">
              <span>{t.radSearch}:</span>
              <span className="text-[#a073e7] bg-[var(--accent-soft)] px-2 py-0.5 rounded-md">
                {chatRadius} {t.radKm}
              </span>
            </div>
            <input
              type="range"
              min="1"
              max="25"
              value={chatRadius}
              onChange={(e) => setChatRadius(Number(e.target.value))}
              className="w-full h-1.5 bg-[var(--border-color)] rounded-lg appearance-none outline-none accent-[#a073e7]"
            />
          </div>
        )}
      </div>

      {/* ОСНОВНИЙ КОНТЕНТ (ВКЛАДКИ) */}
      {!showDmInbox && !activeDmRoom && (
        <>
          {showServices ? (
            <ServicesTab
              displayedServices={displayedServices}
              serviceTab={serviceTab}
              setServiceTab={setServiceTab}
              userCoords={userCoords}
              setSelectedChatUser={setSelectedChatUser}
              handleContactService={(svc: any) => {
                handleStartDM({
                  id: svc.userId,
                  name: svc.author,
                  photo: svc.authorPhoto,
                });
                setShowServices(false);
              }}
              handleReport={handleReport}
              setShowAddServiceForm={setShowAddServiceForm}
            />
          ) : (
            <>
              <div className="px-4 pt-3 pb-2 shrink-0 z-30">
                <div className="flex gap-2">
                  <button
                    onClick={() => setChatTab("global")}
                    className={`flex-1 py-1.5 rounded-xl text-[10px] uppercase font-black border ${
                      chatTab === "global"
                        ? "bg-[var(--inverted-btn)] text-[var(--inverted-text)] border-[var(--inverted-btn)] shadow-sm"
                        : "bg-transparent text-[var(--text-muted)] border-[var(--border-color)]"
                    }`}
                  >
                    {lang === "uk" ? "Стрічка" : "Feed"}
                  </button>
                  <button
                    onClick={() => setChatTab("local")}
                    className={`flex-1 py-1.5 rounded-xl text-[10px] uppercase font-black border ${
                      chatTab === "local"
                        ? "bg-[#a073e7] text-white border-[#a073e7] shadow-sm"
                        : "bg-transparent text-[var(--text-muted)] border-[var(--border-color)]"
                    }`}
                  >
                    {t.inRad}
                  </button>
                </div>
              </div>
              {chatTab === "global" ? (
                <div className="flex-1 overflow-y-auto px-4 pt-2 flex flex-col gap-6 no-scrollbar pb-32">
                  <FeedTab
                    safePosts={safePosts}
                    allUsers={allUsers}
                    setSelectedChatUser={setSelectedChatUser}
                    setCommentPostId={setCommentPostId}
                    handleReport={handleReport}
                  />
                </div>
              ) : (
                <div className="flex flex-col h-full relative">
                  <LocalChatTab
                    displayedChatMessages={displayedChatMessages}
                    isLocating={isLocating}
                    userCoords={userCoords}
                    setSelectedChatUser={setSelectedChatUser}
                    handleReport={handleReport}
                    chatEndRef={chatEndRef}
                  />
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* МОДАЛЬНІ ВІКНА ТА ПОВІДОМЛЕННЯ */}
      {showAdminPanel && isAdmin && (
        <div className="fixed inset-0 z-[600]">
          <Admin onClose={() => setShowAdminPanel(false)} />
        </div>
      )}
      {showDmInbox && !activeDmRoom && (
        <DmInbox
          setShowDmInbox={setShowDmInbox}
          setShowFollowersModal={setShowFollowersModal}
          inboxRooms={inboxRooms}
          setActiveDmRoom={setActiveDmRoom}
        />
      )}
      {activeDmRoom && (
        <DmRoom
          activeDmRoom={activeDmRoom}
          setActiveDmRoom={setActiveDmRoom}
          inboxMessages={inboxMessages}
          setSelectedChatUser={setSelectedChatUser}
        />
      )}
      {showFollowersModal && (
        <CommunityModal
          setShowFollowersModal={setShowFollowersModal}
          knownUsers={knownUsers}
          allUsers={allUsers}
          followingList={followingList}
          setSelectedChatUser={setSelectedChatUser}
          handleStartDM={handleStartDM}
          setShowDmInbox={setShowDmInbox}
        />
      )}
      {selectedChatUser && (
        <PetProfileModal
          selectedChatUser={selectedChatUser}
          setSelectedChatUser={setSelectedChatUser}
          selectedChatPetDetails={selectedChatPetDetails}
          allUsers={allUsers}
          followingList={followingList}
          handleToggleFollow={handleToggleFollow}
          handleStartDM={handleStartDM}
          handleBlockUser={handleBlockUser}
        />
      )}
      {commentPostId && (
        <CommentsModal
          commentPostId={commentPostId}
          setCommentPostId={setCommentPostId}
          posts={posts}
        />
      )}
      {showAddServiceForm && (
        <AddServiceModal
          setShowAddServiceForm={setShowAddServiceForm}
          userCoords={userCoords}
        />
      )}
      {showBugModal && <BugReportModal setShowBugModal={setShowBugModal} />}
      {showEulaModal && <EulaModal setShowEulaModal={setShowEulaModal} />}
    </div>
  );
}
